import React, {
  ReactNode,
  useState,
  useMemo,
  ReactElement,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import MuiDialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import MinimizeIcon from 'assets/svg/minimize';
import MaximizeIcon from 'assets/svg/maximize';
import DownloadIcon from 'assets/svg/download';
import PrintIcon from '@mui/icons-material/Print';
import { isEmptyString } from 'utils/helper';
import isNil from 'lodash/isNil';
import {
  downloadFromUrl,
  printPdfFromUrl,
} from 'utils/files';
import Spinner from 'components/spinner/Spinner';
import { MultiMediaResponse } from 'artifacts/dtos';

type Props = {
  className?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  fullScreen?: boolean;
  media?: MultiMediaResponse;
};

type Maximizable = {
  isFullScreen?: boolean;
};
type MaximizableElement = ReactElement<Maximizable>;

export default function FileViewerDialog({
  open,
  onClose: onDialogClose,
  children: _children,
  media,
}: Props) {
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const FullScreenIcon = useMemo(() => (
    fullScreen ? MinimizeIcon : MaximizeIcon
  ), [fullScreen]);

  const children = React.Children.map<ReactNode, MaximizableElement>(
    _children as MaximizableElement[],
    (child) => (
      React.cloneElement(child as ReactElement<Maximizable>, { isFullScreen: fullScreen })
    ),
  );

  const onClose = () => {
    onDialogClose();
    setTimeout(() => {
      setFullScreen(false);
    }, 500);
  };

  const onFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const onDownload = async () => {
    if (isEmptyString(media?.url?.value)) return;
    try {
      setIsDownloading(true);
      await downloadFromUrl(media?.url?.value ?? '', media?.fileName ?? 'download');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <MuiDialog
      className={clsx('populate-dialog file-viewer', {
        'is-preview': !fullScreen,
      })}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={fullScreen ? false : 'sm'}
      fullScreen={fullScreen}
    >
      <div className="flex flex-row justify-end items-center bg-primaryLight">
        {!isNil(media?.url?.value) && (
          <>
            <IconButton
              onClick={onDownload}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <span className="!w-4 !h-4 flex items-center justify-center">
                  <Spinner
                    name="MoonLoader"
                    size={15}
                    color="#fff"
                  />
                </span>
              ) : (
                <DownloadIcon
                  className="!w-4 !h-4 !text-white"
                />
              )}
            </IconButton>
            <IconButton
              onClick={() => {
                if (!isEmptyString(media?.url?.value)) {
                  printPdfFromUrl(media?.url?.value ?? '');
                }
              }}
              disabled={isDownloading}
            >
              <PrintIcon className="!w-4 !h-4 !text-white" />
            </IconButton>
          </>
        )}
        <IconButton onClick={onFullScreen}>
          <FullScreenIcon className="!w-3.5 !h-3.5 !text-white" />
        </IconButton>
        <IconButton onClick={onClose}>
          <CloseIcon className="!w-5 !h-5 !text-white" />
        </IconButton>
      </div>
      {children}
    </MuiDialog>
  );
}
